import {
  useEditShareMutation,
  useToggleShareMutation,
} from '@carboncopyco/data';
import { ShareOutlineIcon } from '@carboncopyco/icons';
import {
  type IntegrationVendor,
  ShareSourceType,
  type TourShare,
} from '@carboncopyco/types';
import {
  Button,
  Container,
  SkeletonRowLoading,
  TextBase,
  TextButton,
  TextCaption,
  TextTooltip,
  TooltipBubble,
  UserAvatar,
  notify,
} from '@carboncopyco/ui';
import {
  MMMDoYYYY,
  MMMDoYYYYUnix,
  addDaysUnixDate,
  dateUTCFormat,
  dateUTCFromNow,
  isFuture,
  removeUndefinedValues,
} from '@carboncopyco/utils';
import NiceModal from '@ebay/nice-modal-react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type Col, FixedTable } from '../../../FixedTable';
import {
  DEFAULT_EXPIRATION_PERIOD,
  type ShareTourModalState,
  accessDescription,
  accessIcons,
} from '../../../Share';
import { CreateShareTourFlow } from '../../../Share/ShareTourFlow';
import { useUrlState } from '../../../utils';
import { EmptyState } from '../../EmptyState';
import { TableRowWrapper } from '../../TableRowWrapper';
import { INITIAL_URL_STATE } from '../../consts';
import type { UrlState } from '../../types';
import { handleSinglePlural } from '../../utils';
import { useTourPageProps } from '../TourPageContext';
import { LinkActions } from './LinkActions';
import { LinkToggle } from './LinkToggle';

export const LinksTable = () => {
  const {
    organizationId,
    tourId,
    goToLinksTab,
    toursShareAspectRatioEnabled,
    workspaceId,
    tourLinksResponse: {
      items,
      fetchFirstBatch,
      fetchNextBatch,
      isLoading,
      isLoadingBatch,
      error,
      replaceItem,
    },
  } = useTourPageProps();

  const [{ sort, creators, query, active }] =
    useUrlState<UrlState>(INITIAL_URL_STATE);

  const [loadingLinkId, setLoadingLinkId] = useState<TourShare['id']>(null);
  const [toggleShare] = useToggleShareMutation();
  const [editShare] = useEditShareMutation();

  const toggleActive = useCallback(
    (link: TourShare) => {
      const { options, id, isActive } = link;
      const expired = options.expiresAt && !isFuture(options.expiresAt);
      if (expired) {
        editShare({
          organizationId,
          shareId: id,
          ...options,
          cover: options.welcomeMatConfig,
          expiresAt: options.daysToExpire
            ? addDaysUnixDate(options.daysToExpire)
            : addDaysUnixDate(DEFAULT_EXPIRATION_PERIOD),
        })
          .unwrap()
          .then(() => {
            toggleShare({
              organizationId,
              id: Number(id),
              active: true,
            })
              .unwrap()
              .then((response) => {
                const newShare = {
                  ...response,
                  entityType: ShareSourceType.tour,
                  isActive: true,
                };
                replaceItem(id, newShare);
              })
              .catch(() => {
                notify.error('Error updating share status');
              })
              .finally(() => {
                setLoadingLinkId(null);
              });
          });
        return;
      }
      const newShare = {
        ...link,
        entityType: ShareSourceType.tour,
        isActive: !isActive,
      };
      const newIsActive = !isActive;
      replaceItem(id, newShare);
      toggleShare({
        organizationId,
        id: Number(id),
        active: newIsActive,
      })
        .unwrap()
        .catch(() => {
          notify.error('Error updating share status');
          replaceItem(id, {
            ...link,
            entityType: ShareSourceType.tour,
            isActive,
          });
        });
    },
    [editShare, organizationId, replaceItem, toggleShare]
  );

  const handleSubmit = useCallback(
    async (values: ShareTourModalState, link: TourShare) => {
      const {
        linkDescription,
        skipWelcome,
        cover,
        expiresAt,
        daysToExpire,
        security,
        tourAspectRatio,
        autoSyncUpdates,
        salesforceLeads,
        hubspotLeads,
        slackIntegration,
        welcomeMatTitle,
      } = values;
      replaceItem(link.id, {
        ...link,
        entityType: ShareSourceType.tour,
        options: {
          ...link.options,
          linkDescription,
          skipWelcome,
          welcomeMatConfig: cover,
          expiresAt,
          daysToExpire,
          security,
          integrations: Object.keys(
            removeUndefinedValues({
              salesforce: salesforceLeads,
              hubspot: hubspotLeads,
              slack: slackIntegration,
            })
          ) as IntegrationVendor[],
          welcomeMatTitle,
        },
      });
      await editShare({
        linkDescription,
        skipWelcome,
        cover,
        expiresAt,
        daysToExpire,
        security,
        organizationId,
        shareId: link.id,
        autoSyncUpdates,
        tourAspectRatio,
        integrationLeads: Object.keys(
          removeUndefinedValues({
            salesforce: salesforceLeads,
            hubspot: hubspotLeads,
            slack: slackIntegration,
          })
        ) as IntegrationVendor[],
        welcomeMatTitle,
      })
        .unwrap()
        .then(() => {
          notify.success('Link settings were modified successfully');
        })
        .catch(() => {
          replaceItem(link.id, {
            ...link,
            entityType: ShareSourceType.tour,
          });
          notify.error('Failed to edit link settings');
        })
        .finally(() => {
          setLoadingLinkId(null);
        });
    },
    [editShare, organizationId, replaceItem]
  );

  const fetchArgs = useMemo(
    () => ({
      organizationId,
      id: tourId,
      sort,
      creators: creators?.split(','),
      query,
      active,
      workspaces: [workspaceId],
    }),
    [workspaceId, organizationId, tourId, sort, creators, query, active]
  );

  const cols = useMemo<Col<TourShare>[]>(
    () => [
      {
        title: 'Link name',
        width: 174,
        maxContentWidth: 155,
        render: (link) => (
          <TooltipBubble
            title={
              <div className="flex flex-col px-1.5 pb-1 pt-1 leading-none">
                <TextTooltip
                  label={link.options.linkDescription}
                  color="text-ds-natural-50"
                />
                <TextTooltip
                  label={`Created on ${dateUTCFormat(link.updateTime)}`}
                  color="text-ds-natural-50"
                />
              </div>
            }
            placement="top-start"
          >
            <div className="flex flex-col text-ds-natural-600">
              <TextButton
                ellipsis
                label={link.options.linkDescription}
                color="text-ds-natural-600"
              />
              <TextCaption
                label={`${MMMDoYYYY(link.updateTime)}`}
                color="text-ds-natural-300"
                ellipsis
              />
            </div>
          </TooltipBubble>
        ),
      },
      {
        title: 'Shared by',
        alignment: 'center',
        width: 100,
        render: ({ creator }) => (
          <TooltipBubble
            title={creator.name}
            placement="top"
          >
            <UserAvatar
              user={creator}
              size="small"
            />
          </TooltipBubble>
        ),
      },
      {
        title: 'Link type',
        alignment: 'center',
        width: 90,
        render: (link) => {
          const level = link.options.security.level;
          const Icon = accessIcons[level];
          const tooltipText = accessDescription[level];

          return (
            <TooltipBubble
              title={tooltipText}
              placement="top"
            >
              <div className="text-ds-natural-300">
                <Icon size={18} />
              </div>
            </TooltipBubble>
          );
        },
      },
      {
        title: 'Status',
        alignment: 'center',
        width: 110,
        render: (link) => (
          <LinkToggle
            link={link}
            onToggle={toggleActive}
          />
        ),
      },
      {
        title: 'Expires in',
        width: 95,
        alignment: 'center',
        render: ({ options }) => {
          const { expiresAt } = options;
          const expired = expiresAt && !isFuture(expiresAt);
          const label = !expiresAt
            ? '-'
            : expired
              ? 'Expired'
              : dateUTCFromNow(dayjs.unix(expiresAt), true);

          return (
            <TooltipBubble
              title={
                expiresAt &&
                `${!expired ? 'Expires on' : 'Expired on'} ${MMMDoYYYYUnix(expiresAt)}`
              }
              placement="top-start"
              disabled={!expiresAt}
            >
              <TextCaption label={label} />
            </TooltipBubble>
          );
        },
      },
      {
        title: 'Visited',
        alignment: 'right',
        width: 65,
        render: (link) => {
          const visits = link.numberOfAccesses;
          return (
            <TooltipBubble
              title={handleSinglePlural(visits, 'Visited')}
              placement="top"
            >
              <TextBase
                label={visits || '-'}
                color="text-ds-natural-600"
              />
            </TooltipBubble>
          );
        },
      },
      {
        title: 'Completed',
        alignment: 'right',
        width: 94,
        render: (link) => (
          <TextBase
            label={link.totalCompleted}
            color="text-ds-natural-600"
          />
        ),
      },
      {
        title: '',
        alignment: 'right',
        width: 230,
        render: (link) => (
          <LinkActions
            onSubmit={handleSubmit}
            link={link}
            isLoading={link.id === loadingLinkId}
          />
        ),
      },
    ],
    [replaceItem]
  );

  useEffect(() => {
    fetchFirstBatch(fetchArgs);
  }, [fetchFirstBatch, fetchArgs]);

  return (
    <>
      {!isLoading && !items?.length ? (
        <EmptyState
          title="No shares yet"
          description="Once sharing this tour, the links will appear here"
          action={
            <Button
              link
              label="Share tour"
              leftIcon={<ShareOutlineIcon />}
              onClick={() => {
                NiceModal.show(CreateShareTourFlow, {
                  workspaceId,
                  toursShareAspectRatioEnabled: !!toursShareAspectRatioEnabled,
                  organizationId,
                  tourId,
                  goToShares: goToLinksTab,
                });
              }}
            />
          }
        />
      ) : (
        <div className="py-5">
          <FixedTable<TourShare>
            sideSpacing
            rowWrapper={TableRowWrapper}
            cols={cols}
            data={items as TourShare[]}
            handleFetchNextBatch={() => {
              fetchNextBatch(fetchArgs);
            }}
            infiniteListMaxHeight={600}
            isLoading={isLoading}
            isLoadingBatch={isLoadingBatch}
            error={error}
            loader={
              <Container>
                <div className="w-ds-lg-modal">
                  <SkeletonRowLoading
                    medium
                    itemsCount={5}
                  />
                </div>
              </Container>
            }
          />
        </div>
      )}
    </>
  );
};
